import { AlertTriangle, ArrowDownLeft, ArrowUpRight, CalendarClock, LineChart, TrendingUp, Wallet } from 'lucide-react'
import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { PageHeader } from '@/components/common/PageHeader'
import { ErrorState } from '@/components/common/States'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { Skeleton } from '@/components/ui/skeleton'
import { Table, TBody, TD, TH, THead, TR } from '@/components/ui/table'
import { useAuth } from '@/lib/auth/AuthProvider'
import { cn } from '@/lib/utils'
import { fmtDate } from '../shared/format'
import { useCashFlow } from './api'
import { CashFlowChart } from './components/Charts'
import { Kpi, Money, PeriodSelect, Section } from './components/FinanceBits'
import { PaymentFormModal } from './components/PaymentFormModal'
import { FINANCE_PAYMENT_WRITE } from './permissions'
import type { Transaction } from './types'
import { monthLabel, useFinanceLookups, usePeriod } from './ui'
import { ExportMenu } from './components/ExportMenu'
import { exportCashFlowCsv, printCashFlow } from './reports'

export function CashFlowPage() {
  const { t } = useTranslation()
  const { can } = useAuth()
  const { month, setMonth } = usePeriod()
  const flow = useCashFlow(month)
  const { tx, partyName } = useFinanceLookups()
  const [payFor, setPayFor] = useState<Transaction | null>(null)
  const f = flow.data

  return (
    <>
      <PageHeader eyebrow={t('nav.groups.finance')} title={t('finance.cash.title')} description={t('finance.cash.subtitle')} actions={
          <>
            <PeriodSelect value={month} onChange={setMonth} />
            <ExportMenu disabled={!f} onCsv={() => f && exportCashFlowCsv(f)} onPdf={() => f && printCashFlow(f, month)} />
          </>
        }
      />

      {flow.isError ? (
        <ErrorState error={flow.error} onRetry={() => void flow.refetch()} />
      ) : !f ? (
        <div className="space-y-4">
          <Skeleton className="h-28 rounded-xl" />
          <Skeleton className="h-80 rounded-xl" />
        </div>
      ) : (
        <div className="space-y-4">
          <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 xl:grid-cols-4">
            <Kpi icon={Wallet} tone="primary" label={t('finance.cash.current')} value={<Money value={f.cash} />} footer={t('finance.cash.currentHint')} />
            <Kpi icon={ArrowDownLeft} tone="success" label={t('finance.cash.expectedIn')} value={<Money value={f.projected.inflow} />} footer={t('finance.cash.expectedInHint')} />
            <Kpi icon={ArrowUpRight} tone="danger" label={t('finance.cash.expectedOut')} value={<Money value={f.projected.outflow} />} footer={t('finance.cash.expectedOutHint')} />
            <Kpi icon={TrendingUp} tone="info" label={t('finance.cash.projected')} value={<Money value={f.projected.closing} />} footer={t('finance.cash.projectedHint')} />
          </div>

          <div className="grid grid-cols-1 gap-4 xl:grid-cols-[minmax(0,1fr)_24rem]">
            <Section title={t('finance.cash.chartTitle', { period: monthLabel(month) })} icon={LineChart}>
              <CashFlowChart data={f.months} />
            </Section>
            <Section title={t('finance.cash.upcoming')} icon={CalendarClock} bodyClassName="p-0" actions={<Badge tone="info">{f.upcoming.length}</Badge>}>
              {f.upcoming.length === 0 ? (
                <p className="p-6 text-center text-sm text-muted-foreground">{t('finance.cash.noUpcoming')}</p>
              ) : (
                <ul className="max-h-[22rem] divide-y overflow-y-auto scrollbar-none">
                  {f.upcoming.map((u) => {
                    const x = tx.get(u.transactionId)
                    const income = u.kind === 'income'
                    return (
                      <li key={u.transactionId} className="flex items-center gap-3 px-4 py-3">
                        <span className={cn('flex size-8 shrink-0 items-center justify-center rounded-full', income ? 'bg-success/12 text-success' : 'bg-destructive/10 text-destructive')}>
                          {income ? <ArrowDownLeft className="size-4" aria-hidden /> : <ArrowUpRight className="size-4" aria-hidden />}
                        </span>
                        <div className="min-w-0 flex-1">
                          <p className="truncate text-sm font-medium">{x ? partyName(x.counterpartyId) : '—'}</p>
                          <p className={cn('flex items-center gap-1 text-xs', u.overdue ? 'font-medium text-destructive' : 'text-muted-foreground')}>
                            {u.overdue ? <AlertTriangle className="size-3" aria-hidden /> : null}
                            {u.overdue ? t('finance.cash.overdue', { date: fmtDate(u.dueDate) }) : t('finance.cash.due', { date: fmtDate(u.dueDate) })}
                          </p>
                        </div>
                        <div className="text-right">
                          <Money value={u.remaining} className={cn('block text-sm font-semibold', income ? 'text-success' : 'text-destructive')} />
                          {can(FINANCE_PAYMENT_WRITE) && x ? (
                            <Button size="sm" variant="link" className="h-auto p-0 text-xs" onClick={() => setPayFor(x)}>
                              {t(income ? 'finance.pay.addIn' : 'finance.pay.addOut')}
                            </Button>
                          ) : null}
                        </div>
                      </li>
                    )
                  })}
                </ul>
              )}
            </Section>
          </div>

          <Section title={t('finance.cash.tableTitle')} bodyClassName="p-0">
            <div className="@container">
              <ul className="divide-y @2xl:hidden">
                {f.months.map((r) => (
                  <li key={r.month} className="space-y-1 p-4">
                    <p className="font-medium capitalize">{monthLabel(r.month)}</p>
                    <dl className="grid grid-cols-2 gap-x-4 gap-y-1 text-sm">
                      <dt className="text-muted-foreground">{t('finance.cash.inflow')}</dt>
                      <dd className="text-right text-success"><Money value={r.inflow} /></dd>
                      <dt className="text-muted-foreground">{t('finance.cash.outflow')}</dt>
                      <dd className="text-right text-destructive"><Money value={r.outflow} /></dd>
                      <dt className="text-muted-foreground">{t('finance.cash.net')}</dt>
                      <dd className="text-right font-medium"><Money value={r.net} signed /></dd>
                      <dt className="text-muted-foreground">{t('finance.cash.closing')}</dt>
                      <dd className="text-right font-semibold"><Money value={r.closing} /></dd>
                    </dl>
                  </li>
                ))}
              </ul>
              <div className="hidden @2xl:block">
                <Table>
                  <THead>
                    <TR className="hover:bg-transparent">
                      <TH>{t('finance.period')}</TH>
                      <TH className="text-right">{t('finance.cash.inflow')}</TH>
                      <TH className="text-right">{t('finance.cash.outflow')}</TH>
                      <TH className="text-right">{t('finance.cash.net')}</TH>
                      <TH className="text-right">{t('finance.cash.closing')}</TH>
                    </TR>
                  </THead>
                  <TBody>
                    {f.months.map((r) => (
                      <TR key={r.month} className={cn(r.month === month && 'bg-primary/[0.04]')}>
                        <TD className="font-medium capitalize">{monthLabel(r.month)}</TD>
                        <TD className="text-right text-success"><Money value={r.inflow} /></TD>
                        <TD className="text-right text-destructive"><Money value={r.outflow} /></TD>
                        <TD className="text-right font-medium"><Money value={r.net} signed /></TD>
                        <TD className="text-right font-semibold"><Money value={r.closing} /></TD>
                      </TR>
                    ))}
                  </TBody>
                </Table>
              </div>
            </div>
          </Section>
        </div>
      )}

      <PaymentFormModal open={!!payFor} transaction={payFor} onOpenChange={(v) => !v && setPayFor(null)} />
    </>
  )
}
